import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import { getReturnPath } from '../utils/returnPath';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignup, setIsSignup] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const { user, login, signup, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = getReturnPath(location.state?.from);

  useEffect(() => {
    if (user) navigate(from, { replace: true });
  }, [user, navigate, from]);

  if (user) return null;

  const getFirebaseErrorMessage = (err) => {
    const code = err.code || '';
    if (code.includes('user-not-found') || code.includes('wrong-password') || code.includes('invalid-credential')) {
      return 'Please re-enter valid credentials';
    }
    if (code.includes('email-already-in-use')) {
      return 'This email is already registered';
    }
    if (code.includes('weak-password')) {
      return 'Password should be at least 6 characters';
    }
    if (code.includes('too-many-requests')) {
      return 'Too many attempts. Please try again later';
    }
    return err.message || 'An error occurred';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (isSignup) {
        await signup(email, password);
      } else {
        await login(email, password);
      }
      navigate(from, { replace: true });
    } catch (err) {
      const msg = getFirebaseErrorMessage(err);
      setError(msg);
      setModalMessage(msg);
      setShowModal(true);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    try {
      await signInWithGoogle();
      navigate(from, { replace: true });
    } catch (err) {
      const msg = getFirebaseErrorMessage(err);
      setError(msg);
      setModalMessage(msg);
      setShowModal(true);
    }
  };

  return (
    <div className="auth-container">
      <form className="auth-form" onSubmit={handleSubmit}>
        <img src="/log.png" alt="Zimetafsiriwa" className="auth-logo" />
        <h2>{isSignup ? 'Sign Up' : 'Login'}</h2>
        {location.state?.sessionExpired && <p className="auth-session-notice" role="status">Your session has expired. Please sign in again.</p>}
        {error && <p className="auth-error">{error}</p>}
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <button type="submit">{isSignup ? 'Sign Up' : 'Login'}</button>
        <div className="auth-divider"><span>or</span></div>
        <button type="button" className="google-btn" onClick={handleGoogleSignIn}>
          <svg viewBox="0 0 48 48" className="google-icon">
            <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
            <path fill="#FF3D00" d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
            <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
            <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
          </svg>
          Sign in with Google
        </button>
        <p className="auth-toggle" onClick={() => setIsSignup(!isSignup)}>
          {isSignup ? 'Already have an account? Login' : "Don't have an account? Sign Up"}
        </p>
      </form>
      <Modal show={showModal} onClose={() => setShowModal(false)} title="Error">
        <p>{modalMessage}</p>
      </Modal>
    </div>
  );
}
